import { customersSelectors } from 'app-components/flux/customers';
import type { Customer } from 'app-components/flux/customers/customersTypes';
import { projectsSelectors } from 'app-components/flux/projects';
import type { Project } from 'app-components/flux/projects/projectsTypes';
import { getInvoices } from 'app-components/flux/transactions/invoicesSelectors';
import type { TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';
import type RootState from 'app-components/utils/redux-store/rootState';

const normalize = (value: string): string => value.toLowerCase().trim();

const pickSingle = <T>(matches: T[], label: string, name: string, idHint: string): T => {
  if (matches.length === 0) throw new Error(`No ${label} named "${name}" was found.`);
  if (matches.length > 1) {
    throw new Error(`More than one ${label} is named "${name}". Look up the ${idHint} first and pass it instead.`);
  }
  return matches[0];
};

export const resolveCustomer = (
  state: RootState,
  { customerId, customerName }: { customerId?: unknown; customerName?: unknown },
): Customer => {
  const customers = customersSelectors.getCustomersWithArchived(state);

  if (typeof customerId === 'string' && customerId) {
    const customer = customers?.get(customerId);
    if (!customer || customer.isDeleted) throw new Error(`No client with id "${customerId}" was found.`);
    return customer;
  }

  if (typeof customerName === 'string' && customerName.trim()) {
    const target = normalize(customerName);
    const matches = (customers?.valueSeq().toArray() ?? []).reduce<Customer[]>((acc, customer) => {
      if (
        !customer.isDeleted &&
        !customer.isGuest &&
        normalize(customersSelectors.getCustomerDisplayName(customer)) === target
      ) {
        acc.push(customer);
      }
      return acc;
    }, []);
    return pickSingle(matches, 'client', customerName, 'customerId with get_customers');
  }

  throw new Error('A customerId or customerName is required.');
};

export const resolveProject = (
  state: RootState,
  { projectId, projectName }: { projectId?: unknown; projectName?: unknown },
): Project => {
  const projects = projectsSelectors.getProjects(state);

  if (typeof projectId === 'string' && projectId) {
    const project = projects.get(projectId);
    if (!project || project.isDeleted) throw new Error(`No project with id "${projectId}" was found.`);
    return project;
  }

  if (typeof projectName === 'string' && projectName.trim()) {
    const target = normalize(projectName);
    const matches = projects.valueSeq().reduce<Project[]>((acc, project) => {
      if (!project.isDeleted && project.name && normalize(project.name) === target) acc.push(project);
      return acc;
    }, []);
    return pickSingle(matches, 'project', projectName, 'projectId with get_projects');
  }

  throw new Error('A projectId or projectName is required.');
};

export const resolveInvoice = (
  state: RootState,
  { invoiceId, invoiceNo }: { invoiceId?: unknown; invoiceNo?: unknown },
): TransactionRecord => {
  const invoices = getInvoices(state);

  if (typeof invoiceId === 'string' && invoiceId) {
    const invoice = invoices.find((candidate) => candidate.id === invoiceId && !candidate.isDeleted);
    if (!invoice) throw new Error(`No invoice with id "${invoiceId}" was found.`);
    return invoice;
  }

  if ((typeof invoiceNo === 'string' && invoiceNo.trim()) || typeof invoiceNo === 'number') {
    const target = normalize(String(invoiceNo));
    const matches = invoices.filter(
      (candidate) =>
        !candidate.isDeleted && candidate.invoiceNo != null && normalize(String(candidate.invoiceNo)) === target,
    );
    return pickSingle(matches, 'invoice', String(invoiceNo), 'invoiceId with get_invoices');
  }

  throw new Error('An invoiceId or invoiceNo is required.');
};
